import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getActivities, getEditHistory } from '../api';
import {
  errorMessage, useAccountsLookup, useContactsLookup, useOpportunitiesLookup, useUsersLookup,
  useCreateActivity, useUpdateActivity, useDeleteActivity, useCreateAccount, useCreateContact,
} from '../api/queries';
import { useToast } from '../context/ToastContext';
import { useAssistant } from '../assistant/AssistantContext';
import usePagination from '../hooks/usePagination';
import useOpenOnNew from '../hooks/useOpenOnNew';
import Pagination from '../components/Pagination';
import ActivityTimeline from '../components/ActivityTimeline';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { Plus, Sparkles, X, Search, Clock } from 'lucide-react';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';
import MultiSelectFilter from '../components/MultiSelectFilter';
import TypeaheadSelect from '../components/TypeaheadSelect';

// The timeline: every entry the person can see, newest first, a page at a
// time, grouped by day. A captured note shows where it came from and can
// keep the words as said (stored, not shown). The primary way in is the note (⌘J); the form is
// still here for a hand-typed entry.

const TYPE_OPTIONS = ['Call', 'Email', 'Meeting', 'Note', 'Text'].map((t) => ({ id: t, name: t }));
const SOURCE_OPTIONS = [
  { id: 'manual', name: 'Typed in a form' },
  { id: 'web', name: 'Web note' },
  { id: 'ios', name: 'iPhone' },
  { id: 'siri', name: 'Siri' },
];


function Activities() {
  const { canDo } = useAuth();
  const toast = useToast();
  const { openWith } = useAssistant();

  const { data: accounts = [] } = useAccountsLookup();
  const { data: contacts = [] } = useContactsLookup();
  const { data: opportunities = [] } = useOpportunitiesLookup();
  const { data: users = [] } = useUsersLookup();

  const [typeFilter, setTypeFilter] = useState([]);
  const [accountFilter, setAccountFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [personFilter, setPersonFilter] = useState([]);
  const extraParams = useMemo(() => ({
    type: typeFilter.join(',') || undefined,
    account_id: accountFilter.join(',') || undefined,
    source: sourceFilter.join(',') || undefined,
    logged_by: personFilter.join(',') || undefined,
  }), [typeFilter, accountFilter, sourceFilter, personFilter]);

  const {
    data: activities, loading, fetching, error: loadError,
    page, setPage, limit, setLimit, total, totalPages, search, setSearch,
  } = usePagination(getActivities, {
    queryKey: 'activities',
    initialLimit: 25,
    initialSortBy: 'activity_date',
    initialSortOrder: 'desc',
    debounceMs: 300,
    extraParams,
  });

  const createMutation = useCreateActivity();
  const updateMutation = useUpdateActivity();
  const deleteMutation = useDeleteActivity();
  const createAccountMutation = useCreateAccount();
  const createContactMutation = useCreateContact();

  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editHistory, setEditHistory] = useState({});
  const [expandedHistory, setExpandedHistory] = useState({});
  const [formData, setFormData] = useState({ type: 'Call', description: '', account_id: '', contact_id: '', opportunity_id: '', activity_date: new Date().toISOString().slice(0, 16) });
  const [accountSearchText, setAccountSearchText] = useState('');
  const [contactSearchText, setContactSearchText] = useState('');
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  useOpenOnNew(() => setShowCreateModal(true));

  const hasFilters = !!(search || typeFilter.length || accountFilter.length || sourceFilter.length || personFilter.length);
  const clearFilters = () => { setSearch(''); setTypeFilter([]); setAccountFilter([]); setSourceFilter([]); setPersonFilter([]); };

  const resetFormState = () => {
    setModalError('');
    setFormData({ type: 'Call', description: '', account_id: '', contact_id: '', opportunity_id: '', activity_date: new Date().toISOString().slice(0, 16) });
    setAccountSearchText('');
    setContactSearchText('');
    setShowNewAccountForm(false);
    setShowNewContactForm(false);
    setNewAccountName('');
    setNewContactFirstName('');
    setNewContactLastName('');
    setNewContactEmail('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync(formData);
      setShowCreateModal(false);
      resetFormState();
      toast.success('Activity logged');
    } catch (err) { setModalError(errorMessage(err, 'Failed to log activity')); }
  };

  const handleCreateNewAccount = async () => {
    if (!newAccountName.trim()) return;
    try {
      const res = await createAccountMutation.mutateAsync({ name: newAccountName.trim() });
      setFormData((prev) => ({ ...prev, account_id: res.data.id, contact_id: '', opportunity_id: '' }));
      setAccountSearchText(newAccountName.trim());
      setShowNewAccountForm(false);
      setNewAccountName('');
    } catch (err) { setModalError(errorMessage(err, 'Failed to create account')); }
  };

  const handleCreateNewContact = async () => {
    if (!newContactFirstName.trim()) return;
    try {
      const fullName = [newContactFirstName.trim(), newContactLastName.trim()].filter(Boolean).join(' ');
      const res = await createContactMutation.mutateAsync({
        first_name: newContactFirstName.trim(),
        last_name: newContactLastName.trim(),
        name: fullName,
        email: newContactEmail.trim() || undefined,
        account_id: formData.account_id,
      });
      setFormData((prev) => ({ ...prev, contact_id: res.data.id }));
      setContactSearchText(fullName);
      setShowNewContactForm(false);
      setNewContactFirstName('');
      setNewContactLastName('');
      setNewContactEmail('');
    } catch (err) { setModalError(errorMessage(err, 'Failed to create contact')); }
  };

  const filteredContacts = contacts.filter((c) => !formData.account_id || c.account_id === parseInt(formData.account_id, 10));

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(deleteConfirm);
      setDeleteConfirm(null);
      toast.success('Activity deleted');
    } catch (err) { setError(errorMessage(err, 'Failed to delete activity')); }
  };

  const saveEdit = async () => {
    try {
      await updateMutation.mutateAsync({ id: editingActivity.id, data: {
        type: editingActivity.type,
        description: editingActivity.description,
        activity_date: editingActivity.activity_date,
        account_id: editingActivity.account_id,
        contact_id: editingActivity.contact_id,
        opportunity_id: editingActivity.opportunity_id,
      } });
      setEditingActivity(null);
      toast.success('Activity updated');
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to update activity'));
    }
  };

  // Who changed an edited entry, and when: fetched on demand, per entry.
  const renderHistory = (activity) => {
    if (!activity.updated_at) return null;
    const key = activity.id;
    const open = !!expandedHistory[key];
    return (
      <div className="tl-history">
        <button
          type="button"
          className="tl-raw-toggle"
          onClick={async () => {
            if (open) { setExpandedHistory((prev) => ({ ...prev, [key]: false })); return; }
            if (!editHistory[key]) {
              try {
                const res = await getEditHistory('activity', activity.id);
                setEditHistory((prev) => ({ ...prev, [key]: res.data }));
              } catch (e) { /* history is a nicety */ }
            }
            setExpandedHistory((prev) => ({ ...prev, [key]: true }));
          }}
        >
          {open ? 'Hide edit history' : 'Show edit history'}
        </button>
        {open && editHistory[key] && (
          <ul className="tl-history-list">
            {editHistory[key].map((entry) => (
              <li key={entry.id}>{entry.edited_by_name} edited on {new Date(entry.edited_at).toLocaleString()}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const canCreate = canDo('activities', 'create');

  return (
    <div className="activities-page">
      <div className="page-header">
        <h1 className="page-title">Activities</h1>
        {canCreate && (
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={() => setShowCreateModal(true)}><Plus size={18} /> Log Activity</button>
          </div>
        )}
      </div>

      {(error || loadError) && <div className="alert alert-error" role="alert" aria-live="assertive">{error || loadError}</div>}

      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input type="text" className="form-input" placeholder="Search activities..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search activities" />
            </div>
            <MultiSelectFilter label="Types" options={TYPE_OPTIONS} selected={typeFilter} onChange={setTypeFilter} className="filter-dropdown-sm" ariaLabel="Filter by type" />
            <MultiSelectFilter label="Accounts" options={accounts} selected={accountFilter} onChange={setAccountFilter} className="filter-dropdown-md" ariaLabel="Filter by account" />
            <MultiSelectFilter label="Source" options={SOURCE_OPTIONS} selected={sourceFilter} onChange={setSourceFilter} className="filter-dropdown-sm" ariaLabel="Filter by source" />
            <MultiSelectFilter label="People" options={users} selected={personFilter} onChange={setPersonFilter} className="filter-dropdown-md" ariaLabel="Filter by who logged it" />
            {hasFilters && (
              <button className="btn-clear-filters" onClick={clearFilters}>
                <X size={14} />
                Clear
              </button>
            )}
            {fetching && !loading && <span className="text-muted text-sm">Updating…</span>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="loading-container"><div className="spinner"></div></div>
          ) : activities.length === 0 ? (
            hasFilters ? (
              <EmptyStateFiltered onClearFilters={clearFilters} />
            ) : (
              <EmptyState
                icon={Clock}
                title="No activities yet"
                description="Open the AI Assistant and say what happened. The AI drafts the entry; you approve it."
                actionLabel={canCreate ? 'Open the AI Assistant' : undefined}
                onAction={canCreate ? () => openWith('', { send: false }) : undefined}
                actionIcon={Sparkles}
              />
            )
          ) : (
            <ActivityTimeline
              activities={activities}
              canEdit={canDo('activities', 'edit')}
              canDelete={canDo('activities', 'delete')}
              onEdit={(a) => setEditingActivity({ ...a })}
              onDelete={(a) => setDeleteConfirm(a.id)}
              renderExtra={renderHistory}
            />
          )}
        </div>
        {!loading && activities.length > 0 && (
          <Pagination page={page} totalPages={totalPages} total={total} limit={limit} onPageChange={setPage} onLimitChange={setLimit} />
        )}
      </div>

      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetFormState(); }} title="Log Activity">
        <form onSubmit={handleCreate}>
          <div className="modal-body">
            {modalError && <div className="alert alert-error" role="alert">{modalError}</div>}
            <p className="text-muted text-sm" style={{ marginBottom: 12 }}>Prefer to just say what happened? Close this and open the AI Assistant.</p>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label" htmlFor="activity-type">Type *</label>
                <select id="activity-type" className="form-select" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} required aria-required="true">
                  {TYPE_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="activity-date">Date/Time</label>
                <input id="activity-date" type="datetime-local" className="form-input" value={formData.activity_date} onChange={(e) => setFormData({ ...formData, activity_date: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="activity-account">Account *</label>
              {showNewAccountForm ? (
                <div className="inline-create-form">
                  <input type="text" className="form-input" placeholder="Account name *" value={newAccountName} onChange={(e) => setNewAccountName(e.target.value)} autoFocus />
                  <div className="inline-create-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNewAccount} disabled={!newAccountName.trim()}>Create</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowNewAccountForm(false); setNewAccountName(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <TypeaheadSelect
                  id="activity-account"
                  required
                  placeholder="Type to search accounts..."
                  items={accounts}
                  value={formData.account_id}
                  displayValue={accountSearchText}
                  onChange={(id, name) => {
                    setFormData({ ...formData, account_id: id, contact_id: '', opportunity_id: '' });
                    setAccountSearchText(name);
                    setContactSearchText('');
                  }}
                  onAddNew={() => setShowNewAccountForm(true)}
                  addNewLabel="Add New Account"
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="activity-contact">Contact</label>
              {showNewContactForm ? (
                <div className="inline-create-form">
                  <div className="grid grid-cols-2">
                    <input type="text" className="form-input" placeholder="First name *" value={newContactFirstName} onChange={(e) => setNewContactFirstName(e.target.value)} autoFocus />
                    <input type="text" className="form-input" placeholder="Last name" value={newContactLastName} onChange={(e) => setNewContactLastName(e.target.value)} />
                  </div>
                  <input type="email" className="form-input" placeholder="Email (optional)" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} style={{ marginTop: '8px' }} />
                  <div className="inline-create-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNewContact} disabled={!newContactFirstName.trim()}>Create</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowNewContactForm(false); setNewContactFirstName(''); setNewContactLastName(''); setNewContactEmail(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <TypeaheadSelect
                  id="activity-contact"
                  placeholder={formData.account_id ? 'Type to search contacts...' : 'Select an account first'}
                  items={filteredContacts}
                  value={formData.contact_id}
                  displayValue={contactSearchText}
                  onChange={(id, name) => { setFormData({ ...formData, contact_id: id }); setContactSearchText(name); }}
                  onAddNew={formData.account_id ? () => setShowNewContactForm(true) : undefined}
                  addNewLabel="Add New Contact"
                  disabled={!formData.account_id}
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="activity-opportunity">Opportunity</label>
              <select id="activity-opportunity" className="form-select" value={formData.opportunity_id} onChange={(e) => setFormData({ ...formData, opportunity_id: e.target.value })} disabled={!formData.account_id}>
                <option value="">None</option>
                {opportunities.filter((o) => !formData.account_id || o.account_id === parseInt(formData.account_id, 10)).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="activity-description">Description</label>
              <textarea id="activity-description" className="form-textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Add notes about this activity..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowCreateModal(false); resetFormState(); }}>Cancel</button>
            <button type="submit" className="btn btn-primary">Log Activity</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} onConfirm={handleDelete} title="Delete Activity" message="Delete this activity?" confirmText="Delete" />

      <Modal isOpen={!!editingActivity} onClose={() => setEditingActivity(null)} title="Edit Activity">
        {editingActivity && (
          <>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label" htmlFor="edit-activity-type">Type</label>
                <select id="edit-activity-type" className="form-select" value={editingActivity.type} onChange={(e) => setEditingActivity({ ...editingActivity, type: e.target.value })}>
                  {TYPE_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="edit-activity-date">Date/Time</label>
                <input id="edit-activity-date" type="datetime-local" className="form-input" value={editingActivity.activity_date ? editingActivity.activity_date.slice(0, 16) : ''} onChange={(e) => setEditingActivity({ ...editingActivity, activity_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="edit-activity-description">Description</label>
                <textarea id="edit-activity-description" className="form-textarea" rows={4} value={editingActivity.description || ''} onChange={(e) => setEditingActivity({ ...editingActivity, description: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingActivity(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default Activities;
